import React, { useContext, useEffect, useRef, useState } from 'react';
import Isvg from 'react-inlinesvg';
import PropTypes from 'prop-types';

import AutocompleteSearch from 'components/AutocompleteSearch';
import { Card } from 'components/Card';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import SvgQuestion from 'components/Svgs/Question';
import When from 'components/When';
import { ICONS, ILLUSTRATIONS } from 'constants/icons';
import LINKS from 'constants/links';
import URLS from 'constants/urls';
import { Column, ColumnLayout, Title } from 'features/App';
import appMessages from 'features/App/App.messages';
import { PageLayoutContext } from 'features/App/Contexts';
import { bem } from 'helpers/class-name';
import { transformV2ClientResponse } from 'helpers/core';
import { httpClient } from 'helpers/http';
import { makePath } from 'helpers/link-generation';

import { INTEGRATOR_FILTERS } from './Integrations.constants';
import { integrationsMessages as messages } from './Integrations.messages';
import IntegratorCard from './IntegratorCard/IntegratorCard';

import './Integrations.scss';

const { block, element } = bem('integrations');

const SearchBar = ({
  filter,
  handleSearchOpen,
  handleFilterAll,
  handleFilterAppointments,
  handleFilterClasses,
  handleFilterFoodAndBev,
  handleSearchChange,
  isMobile,
  searchOpen,
  suggestions,
}) => {
  const searchInputRef = React.useRef(null);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const appointmentsFilterIcon = filter === INTEGRATOR_FILTERS.APPOINTMENTS ? ICONS.calendarBlue : ICONS.calendarDark;
  const classesFilterIcon = filter === INTEGRATOR_FILTERS.CLASSES ? ICONS.dumbbellBlue : ICONS.dumbbellDark;
  const foodAndBeverageFilterIcon =
    filter === INTEGRATOR_FILTERS.FOOD_AND_BEVERAGE ? ICONS.foodAndBeverageBlue : ICONS.foodAndBeverage;

  return (
    <div className={element('search')}>
      <When predicate={searchOpen}>
        <div className={element('search-input', 'anim', 'anim--fadeIn')}>
          <AutocompleteSearch
            ref={searchInputRef}
            options={suggestions}
            placeholder={messages.searchPlaceholder}
            onChange={handleSearchChange}
            onBlur={handleSearchOpen}
          />
        </div>
      </When>
      <When predicate={!searchOpen}>
        <div className={element('search-container', 'anim', 'anim--fadeIn')}>
          <div className={element('filter-container')}>
            <Message
              as="button"
              className={element('filter', { selected: filter === INTEGRATOR_FILTERS.ALL })}
              message={isMobile ? messages.allIntegrationsMobile : messages.allIntegrationsDesktop}
              onClick={handleFilterAll}
            />
            <button
              type="button"
              className={element('filter', { selected: filter === INTEGRATOR_FILTERS.APPOINTMENTS })}
              onClick={handleFilterAppointments}>
              <Isvg className={element('filter-icon')} src={appointmentsFilterIcon} />
              <Message message={messages.filterAppointments} />
            </button>
            <button
              type="button"
              className={element('filter', { selected: filter === INTEGRATOR_FILTERS.CLASSES })}
              onClick={handleFilterClasses}>
              <Isvg className={element('filter-icon')} src={classesFilterIcon} />
              <Message message={messages.filterClasses} />
            </button>
            <button
              type="button"
              className={element('filter', { selected: filter === INTEGRATOR_FILTERS.FOOD_AND_BEVERAGE })}
              onClick={handleFilterFoodAndBev}>
              <Isvg className={element('filter-icon')} src={foodAndBeverageFilterIcon} />
              <Message message={messages.filterFoodAndBev} />
            </button>
          </div>
          <div className={element('search-icon-container')}>
            <button aria-label="Open search" type="button" onClick={handleSearchOpen}>
              <Isvg className={element('search-icon')} src={ICONS.searchGlass} />
            </button>
          </div>
        </div>
      </When>
    </div>
  );
};

SearchBar.propTypes = {
  filter: PropTypes.string,
  handleSearchOpen: PropTypes.func,
  handleFilterAll: PropTypes.func,
  handleFilterAppointments: PropTypes.func,
  handleFilterClasses: PropTypes.func,
  handleFilterFoodAndBev: PropTypes.func,
  handleSearchChange: PropTypes.func,
  isMobile: PropTypes.bool,
  searchOpen: PropTypes.bool,
  suggestions: PropTypes.arrayOf(PropTypes.string),
};

const IntegrationsBanner = ({ locale }) => {
  return (
    <Card flush className={element('banner-card', 'card--no-border')}>
      <div className={element('banner-content')}>
        <Message as="h1" message={messages.bannerTitle} />
        <Message as="p" message={messages.bannerSubtitle} />
        <Message
          as="a"
          href={makePath.integratorsHelp(locale)}
          target="_blank"
          rel="noopener noreferrer"
          message={appMessages.learnMore}
        />
      </div>
      <div className={element('banner-bullets')}>
        <div className={element('bullet-wrapper')}>
          <Isvg src={ILLUSTRATIONS.gift} />
          <Message className={element('bullet-text')} message={messages.bannerBulletOne} />
          <a
            className={element('smartspot-link')}
            href={LINKS.smartspot.learnMore}
            target="_blank"
            rel="noopener noreferrer">
            <SvgQuestion className={element('question-mark')} />
          </a>
        </div>
        <div className={element('bullet-wrapper')}>
          <Isvg src={ILLUSTRATIONS.duplicate} />
          <Message className={element('bullet-text')} message={messages.bannerBulletTwo} />
        </div>
        <div className={element('bullet-wrapper')}>
          <Isvg src={ILLUSTRATIONS.rocket} />
          <Message className={element('bullet-text')} message={messages.bannerBulletThree} />
        </div>
      </div>
    </Card>
  );
};

IntegrationsBanner.propTypes = {
  locale: PropTypes.string,
};

const Integrations = ({ isDesktop, isMobile, createErrorBanner, locale, onIntegratorCardClick }) => {
  const [filter, setFilter] = useState(INTEGRATOR_FILTERS.ALL);
  const [integratorsDetails, setIntegratorsDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearchAtTop, setIsSearchAtTop] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { setHeaderTop } = useContext(PageLayoutContext);

  const gridWrapper = useRef(null);
  const searchWrapper = useRef(null);

  const HEADER_HEIGHT = 64;
  const DESKTOP_HEADER_HEIGHT = 72;

  useEffect(() => {
    const fetchIntegratorsDetails = async () => {
      try {
        const response = await httpClient.get(URLS.integrators.detailList);
        const responseData = transformV2ClientResponse(response);
        setIntegratorsDetails(responseData.integrators);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        return createErrorBanner(messages.loadError);
      }
    };

    fetchIntegratorsDetails();

    /** The handleScroll function adjusts the position of the header on scroll
     * so the search element appears to push the header out of the way.
     */
    const handleScroll = () => {
      if (!searchWrapper.current) return;
      const searchPosition = searchWrapper.current.getBoundingClientRect();

      if (searchPosition.top <= (isDesktop ? DESKTOP_HEADER_HEIGHT : HEADER_HEIGHT)) {
        setHeaderTop(searchPosition.top - (isDesktop ? DESKTOP_HEADER_HEIGHT : HEADER_HEIGHT));
      } else {
        setHeaderTop(0);
      }

      if (searchPosition.top === 0) {
        setIsSearchAtTop(true);
      } else {
        setIsSearchAtTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const integratorsSuggestions = !isLoading ? integratorsDetails.map(integrator => integrator.name) : [];

  const scrollGridAfterChange = () => {
    if (!gridWrapper.current) return;
    const grid = gridWrapper.current;
    if (grid.getBoundingClientRect().top < HEADER_HEIGHT) {
      grid.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSearchChange = value => {
    setFilter(INTEGRATOR_FILTERS.SEARCH);
    setSearchTerm(value);
    scrollGridAfterChange();
  };

  const handleFilterChange = filterType => {
    setFilter(filterType);
    scrollGridAfterChange();
  };

  let filteredIntegrators = [];

  switch (filter) {
    case INTEGRATOR_FILTERS.SEARCH:
      filteredIntegrators = integratorsDetails.filter(integrator =>
        integrator.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      break;
    case INTEGRATOR_FILTERS.APPOINTMENTS:
      filteredIntegrators = integratorsDetails.filter(integrator => integrator.supportsAppointments);
      break;
    case INTEGRATOR_FILTERS.CLASSES:
      filteredIntegrators = integratorsDetails.filter(integrator => integrator.supportsClasses);
      break;
    case INTEGRATOR_FILTERS.FOOD_AND_BEVERAGE:
      filteredIntegrators = integratorsDetails.filter(integrator => integrator.supportsFoodAndBev);
      break;
    case INTEGRATOR_FILTERS.ALL:
    default:
      filteredIntegrators = integratorsDetails;
  }

  const handleSearchOpen = () => {
    // If the search is open and there are no results, reset the filter to all on change
    if (searchOpen && filteredIntegrators.length === 0) {
      setFilter(INTEGRATOR_FILTERS.ALL);
    }
    setSearchOpen(!searchOpen);
  };

  return (
    <ColumnLayout className={block()}>
      <Column>
        <Message as={Title} message={messages.title} />
        <div className={element('wrapper')}>
          <IntegrationsBanner locale={locale} />
        </div>
        <div className={element('search-wrapper', { header: isSearchAtTop, searchOpen })} ref={searchWrapper}>
          <SearchBar
            filter={filter}
            handleFilterAll={() => handleFilterChange(INTEGRATOR_FILTERS.ALL)}
            handleFilterAppointments={() => handleFilterChange(INTEGRATOR_FILTERS.APPOINTMENTS)}
            handleFilterClasses={() => handleFilterChange(INTEGRATOR_FILTERS.CLASSES)}
            handleFilterFoodAndBev={() => handleFilterChange(INTEGRATOR_FILTERS.FOOD_AND_BEVERAGE)}
            handleSearchChange={handleSearchChange}
            handleSearchOpen={handleSearchOpen}
            isMobile={isMobile}
            searchOpen={searchOpen}
            searchTerm={searchTerm}
            suggestions={integratorsSuggestions}
          />
        </div>
        <div className={element('wrapper')}>
          <div className={element('grid-wrapper')} ref={gridWrapper}>
            <div className="grid">
              <When predicate={isLoading}>
                <LoadingDots />
              </When>
              <When predicate={!isLoading}>
                {filteredIntegrators.map(integrator => (
                  <div key={integrator.name} className="grid__item 1/1 md-1/2 lg-1/3 mb-">
                    <IntegratorCard
                      onClick={() => onIntegratorCardClick(integrator)}
                      name={integrator.name}
                      description={integrator.description}
                      logoImageUrl={integrator.logoImageUrl}
                      supportsAppointments={integrator.supportsAppointments}
                      supportsClasses={integrator.supportsClasses}
                      supportsFoodAndBev={integrator.supportsFoodAndBev}
                    />
                  </div>
                ))}
              </When>
            </div>
          </div>
        </div>
      </Column>
    </ColumnLayout>
  );
};

Integrations.propTypes = {
  isDesktop: PropTypes.bool,
  isMobile: PropTypes.bool,
  createErrorBanner: PropTypes.func,
  locale: PropTypes.string,
  onIntegratorCardClick: PropTypes.func,
};

export default Integrations;
